import React, { useEffect } from "react";
import { useInView } from "react-intersection-observer";

import type { ProductCard } from "types/product";

import { useAnalyticsViewItemList } from "~/providers/AnalyticsViewItemListProvider";

export type ProductCardVisibilityObserverProps = Pick<
  ProductCard,
  "googleAnalyticsItem"
> & {
  /** 0 based index for the position this card occupies within the parent list */
  index: number;
  /** Function for children with ref */
  children: (props: { ref: React.Ref<any> }) => React.ReactNode;
  /**
   * Set to true if we know that this card is already in view.
   * This can help in situations where you know the item will appear in view, but the
   * intersection observer is not firing.
   */
  initialInView?: boolean;
};

/**
 * Wraps a product card to emit a view_item_list analytics event, when the product
 * card becomes visible to the user.
 */
export function ProductCardVisibilityObserver({
  index,
  children,
  initialInView,
  googleAnalyticsItem,
}: ProductCardVisibilityObserverProps) {
  const analyticsViewItemList = useAnalyticsViewItemList();

  const { ref, inView } = useInView({ initialInView });

  useEffect(() => {
    // Card is not in view, do nothing.
    if (!inView) return;

    // Card is not wrapped by AnalyticsViewItemListProvider, do nothing.
    if (!analyticsViewItemList) return;

    // Push item to AnalyticsViewItemListProvider.
    analyticsViewItemList.pushItem({
      ...googleAnalyticsItem,
      // The Google Analytics event uses a 1 based index.
      index: index + 1,
    });
  }, [inView, analyticsViewItemList, index, googleAnalyticsItem]);

  return children({ ref });
}
